<template>
  <div>
    <!-- Table Container Card -->
    <b-card no-body class="mb-0">
      <div class="m-2">
        <!-- Table Top: filtros en una sola línea -->
        <b-row align-v="end">
          <!-- Per Page -->
          <b-col
            cols="auto"
            class="d-flex align-items-center mb-1 mb-md-0"
          >
            <label class="mb-0 mr-50">Mostrar</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block"
            />
          </b-col>

          <!-- Estado -->
          <b-col cols="12" md="">
            <label>Estado</label>
            <v-select
              v-model="statusFilter"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="estadoOptions"
              :reduce="(o) => o.value"
              label="label"
              :clearable="false"
              placeholder="Todos"
            />
          </b-col>

          <!-- Proveedor -->
          <b-col cols="12" md="">
            <label>Proveedor</label>
            <v-select
              v-model="proveedorFilter"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="proveedores"
              :reduce="(p) => p.id"
              label="nombre_comercial"
              :clearable="true"
              placeholder="Todos"
            />
          </b-col>

          <!-- Buscar -->
          <b-col cols="12" md="">
            <label>Buscar</label>
            <b-form-input
              v-model="searchQuery"
              placeholder="Nº de pedido o fecha"
            />
          </b-col>

          <!-- Crear -->
          <b-col cols="auto" class="d-flex align-items-end">
            <b-button variant="primary" @click="editMaterialesRecepcion('new')">
              <span class="text-nowrap">Recepción de material</span>
            </b-button>
          </b-col>
        </b-row>
      </div>

      <b-table
        ref="refMaterialesRecepcionListTable"
        class="position-relative"
        :items="fetchMaterialesRecepcion"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        empty-text="No se encontraron registros coincidentes"
        :sort-desc.sync="isSortDirDesc"
        :busy="!fetchMaterialesRecepcion"
      >
        <!-- Table Loading -->
        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle" small />
            <strong class="ml-1">Cargando datos...</strong>
          </div>
        </template>

        <!-- Nº Pedido con enlace al PP -->
        <template #cell(num_pedido_proveedor)="data">
          <span v-if="!data.item.fk_pedido_proveedor">
            {{ data.item.num_pedido_proveedor || '-' }}
          </span>
          <router-link
            v-else
            target="_blank"
            :to="{ name: 'pedido-proveedor-edit', params: { id: data.item.fk_pedido_proveedor } }"
          >
            {{ data.item.num_pedido_proveedor || `PP-${data.item.fk_pedido_proveedor}` }}
            <feather-icon icon="ExternalLinkIcon" size="14" class="align-top ml-25" />
          </router-link>
        </template>

        <!-- Fecha recepción d-m-Y -->
        <template #cell(fecha_recepcion)="data">
          <span>{{ formatDate(data.item.fecha_recepcion) }}</span>
        </template>

        <!-- Fecha envío PP d-m-Y -->
        <template #cell(pp_fecha_enviado)="data">
          <span>{{ data.item.pp_fecha_enviado ? formatDate(data.item.pp_fecha_enviado) : '-' }}</span>
        </template>

        <!-- Proveedor -->
        <template #cell(fk_proveedor)="data">
          <span>{{ data.item.proveedor_name }}</span>
        </template>

        <!-- Estado badge -->
        <template #cell(estado)="data">
          <b-badge
            pill
            :variant="`light-${resolveEstadoVariant(data.item.estado)}`"
            class="text-capitalize"
          >
            {{ data.item.estado || 'pendiente' }}
          </b-badge>
        </template>

        <!-- Acciones -->
        <template #cell(actions)="data">
          <!-- Editar (pendiente/parcial) o Ver (completada) -->
          <b-button
            :id="`edit-${data.item.id}`"
            :variant="data.item.estado === 'completada' ? 'info' : 'success'"
            class="btn-icon mr-1 mb-1"
            @click="editMaterialesRecepcion(data.item.id)"
          >
            <feather-icon :icon="data.item.estado === 'completada' ? 'EyeIcon' : 'EditIcon'" />
          </b-button>
          <b-tooltip
            :target="`edit-${data.item.id}`"
            triggers="hover"
            placement="bottom"
            boundary="window"
          >
            <b>{{ data.item.estado === 'completada' ? 'Ver' : 'Editar' }}</b>
          </b-tooltip>

          <!-- Imprimir PDF -->
          <b-button
            :id="`print-${data.item.id}`"
            variant="secondary"
            class="btn-icon mr-1 mb-1"
            @click="imprimirRecepcion(data.item.id)"
          >
            <feather-icon icon="PrinterIcon" />
          </b-button>
          <b-tooltip
            :target="`print-${data.item.id}`"
            triggers="hover"
            placement="bottom"
            boundary="window"
          >
            <b>Imprimir albarán</b>
          </b-tooltip>

          <!-- Eliminar: solo si pendiente y sin pedido a proveedor vinculado -->
          <b-button
            v-if="data.item.estado === 'pendiente' && !data.item.fk_pedido_proveedor"
            :id="`delete-${data.item.id}`"
            variant="danger"
            class="btn-icon mb-1"
            @click="confirmDelete(data.item)"
          >
            <feather-icon icon="Trash2Icon" />
          </b-button>
          <b-tooltip
            v-if="data.item.estado === 'pendiente' && !data.item.fk_pedido_proveedor"
            :target="`delete-${data.item.id}`"
            triggers="hover"
            placement="bottom"
            boundary="window"
          >
            <b>Eliminar</b>
          </b-tooltip>
        </template>
      </b-table>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">
              Mostrando {{ dataMeta.from }} a {{ dataMeta.to }} de
              {{ dataMeta.of }} entradas
            </span>
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalMaterialesRecepcion"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <!-- Confirmación de borrado -->
    <confirmation-modal
      :show="deleteModal"
      title="Eliminar recepción"
      message="¿Seguro que deseas eliminar esta recepción? Esta acción no se puede deshacer."
      @close="deleteModal = false"
      @action="deleteRecepcion"
    />
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BLink,
  BBadge,
  BPagination,
  BSpinner,
  BTooltip,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import store from '@/store'
import { onUnmounted, ref } from '@vue/composition-api'
import useMaterialesRecepcionList from './useMaterialesRecepcionList'
import materialesStoreModule from './materialesRecepcionStoreModule'
import useProveedoresList from '@/views/system/proveedor/useProveedoresList'
import proveedorStoreModule from '@/views/system/proveedor/proveedorStoreModule'
import ConfirmationModal from '@/components/ConfirmationModal.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BLink,
    BBadge,
    BPagination,
    BSpinner,
    BTooltip,

    vSelect,
    ConfirmationModal,
  },
  data() {
    return {
      deleteModal: false,
      selectedItem: {},
      estadoOptions: [
        { label: 'Todos', value: '' },
        { label: 'Pendiente', value: 'pendiente' },
        { label: 'Parcial', value: 'parcial' },
        { label: 'Completada', value: 'completada' },
      ],
    }
  },
  methods: {
    editMaterialesRecepcion(id) {
      this.$router.push({
        name: 'materiales-recepcion-edit',
        params: { id },
      })
    },
    formatDate(date) {
      if (!date) return '-'
      const d = new Date(date)
      const day = String(d.getDate()).padStart(2, '0')
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const year = d.getFullYear()
      return `${day}-${month}-${year}`
    },
    resolveEstadoVariant(estado) {
      const map = { completada: 'success', parcial: 'warning', pendiente: 'secondary' }
      return map[estado] || 'secondary'
    },
    imprimirRecepcion(id) {
      this.$store
        .dispatch('app-materiales-recepcion/printRecepcion', id)
        .then(response => {
          if (response?.route) window.open(response.route, '_blank')
        })
        .catch(error => {
          console.error('Error al generar el PDF:', error)
        })
    },
    confirmDelete(item) {
      this.selectedItem = item
      this.deleteModal = true
    },
    deleteRecepcion() {
      this.$store
        .dispatch('app-materiales-recepcion/deleteMaterial', this.selectedItem.id)
        .then(() => {
          this.refetchData()
        })
    },
  },
  setup() {
    const MATERIALES_RECEPCION_APP_STORE_MODULE_NAME = 'app-materiales-recepcion'
    const PROVEEDOR_APP_STORE_MODULE_NAME = 'app-proveedor'

    if (!store.hasModule(MATERIALES_RECEPCION_APP_STORE_MODULE_NAME)) {
      store.registerModule(MATERIALES_RECEPCION_APP_STORE_MODULE_NAME, materialesStoreModule)
    }
    if (!store.hasModule(PROVEEDOR_APP_STORE_MODULE_NAME)) {
      store.registerModule(PROVEEDOR_APP_STORE_MODULE_NAME, proveedorStoreModule)
    }

    onUnmounted(() => {
      if (store.hasModule(MATERIALES_RECEPCION_APP_STORE_MODULE_NAME))
        store.unregisterModule(MATERIALES_RECEPCION_APP_STORE_MODULE_NAME)
      if (store.hasModule(PROVEEDOR_APP_STORE_MODULE_NAME))
        store.unregisterModule(PROVEEDOR_APP_STORE_MODULE_NAME)
    })

    const {
      fetchMaterialesRecepcion,
      tableColumns,
      perPage,
      currentPage,
      totalMaterialesRecepcion,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refMaterialesRecepcionListTable,
      refetchData,
      statusFilter,
      proveedorFilter,
    } = useMaterialesRecepcionList()

    const proveedores = ref([])
    const { listProveedores } = useProveedoresList()
    listProveedores().then(res => { proveedores.value = res })

    return {
      fetchMaterialesRecepcion,
      tableColumns,
      perPage,
      currentPage,
      totalMaterialesRecepcion,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refMaterialesRecepcionListTable,
      refetchData,
      statusFilter,
      proveedorFilter,
      proveedores,
    }
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
